import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared";

const SOURCE_LABELS = {
  webchat: "Website Chat", website_widget: "Website Chat", website_form: "Website Form",
  google_business: "Google Business", referral: "Referral", web_search: "Web Search",
  social_media: "Social Media", friend: "Friend", email: "Email", manual: "Manual Entry",
  unspecified: "Unspecified",
};
const LIFECYCLE_TONE = {
  lead: "bg-blue-50 text-blue-700", prospect: "bg-sky-50 text-sky-700",
  active_client: "bg-emerald-50 text-emerald-700", inactive_client: "bg-slate-100 text-slate-500",
};
const initialsOf = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
const relDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 86400000;
  if (diff < 1) return "today";
  if (diff < 2) return "yesterday";
  if (diff < 7) return `${Math.floor(diff)}d ago`;
  return d.toLocaleDateString();
};

function Kpi({ label, value, sub, icon, tone = "text-slate-900", ring = "ring-slate-200", onClick, testid }) {
  const Ic = Icons[icon] || Icons.Circle;
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left bg-white border border-slate-200 rounded-xl p-4 ring-1 ${ring} hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${onClick ? "cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide leading-tight">{label}</span>
        <Ic className={`h-4 w-4 shrink-0 ${tone}`} />
      </div>
      <div className={`mt-2 text-2xl font-bold ${tone}`}>{value ?? 0}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-400">{sub}</div>}
    </button>
  );
}

function Panel({ title, icon, tone = "text-slate-500", children, testid, action }) {
  const Ic = Icons[icon] || Icons.Square;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Ic className={`h-4 w-4 ${tone}`} />
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function ContactRow({ c, onOpen }) {
  return (
    <button onClick={() => onOpen(c.id)} data-testid={`crm-dash-contact-${c.id}`}
      className="w-full flex items-center gap-3 py-2 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors text-left">
      <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{initialsOf(c.full_name)}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">{c.full_name}</div>
        <div className="text-[11px] text-slate-400 truncate">{c.client_number || c.email || "—"}</div>
      </div>
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${LIFECYCLE_TONE[c.lifecycle_status] || "bg-slate-100 text-slate-500"}`}>
        {(c.lifecycle_status || "").replace("_", " ") || "—"}
      </span>
      <span className="text-[11px] text-slate-400 w-16 text-right shrink-0">{relDate(c.last_activity_at)}</span>
    </button>
  );
}

export default function CRMDashboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const open = (id) => navigate(`/client-workspace/${id}`);

  // Legacy redirect: /crm?tab=leads|active-clients|inactive-clients -> sub-pages
  useEffect(() => {
    const tab = params.get("tab");
    const map = { leads: "/crm/leads", active: "/crm/active-clients", "active-clients": "/crm/active-clients",
      inactive: "/crm/inactive-clients", "inactive-clients": "/crm/inactive-clients" };
    if (tab && map[tab]) navigate(map[tab], { replace: true });
  }, []); // eslint-disable-line

  useEffect(() => {
    const load = () => api.get("/crm/dashboard").then((r) => setD(r.data)).catch(() => setErr(true));
    load(); const t = setInterval(load, 30000); return () => clearInterval(t);
  }, []);

  const k = d?.kpis || {};
  const sources = d?.contact_source || [];
  const maxSrc = sources.reduce((m, s) => Math.max(m, s.count), 1);
  const teamSummary = d?.team_summary || [];

  return (
    <div data-testid="crm-dashboard">
      <PageHeader title="CRM" description="Relationship activity across the entire firm — leads, clients and conversions." />

      {err && <div className="text-sm text-rose-600 mb-4" data-testid="crm-dashboard-error">Couldn't load CRM metrics. Retrying…</div>}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        <Kpi label="Total Leads" value={k.total_leads} icon="UserPlus" tone="text-blue-600" onClick={() => navigate("/crm/leads")} testid="crm-kpi-total-leads" />
        <Kpi label="New Leads Today" value={k.new_leads_today} icon="Sparkles" tone="text-orange-600" onClick={() => navigate("/crm/leads")} testid="crm-kpi-new-today" />
        <Kpi label="Leads This Week" value={k.leads_this_week} icon="CalendarDays" onClick={() => navigate("/crm/leads")} testid="crm-kpi-week" />
        <Kpi label="Active Clients" value={k.active_clients} icon="Users" tone="text-emerald-600" onClick={() => navigate("/crm/active-clients")} testid="crm-kpi-active" />
        <Kpi label="Inactive Clients" value={k.inactive_clients} icon="UserMinus" tone="text-slate-500" onClick={() => navigate("/crm/inactive-clients")} testid="crm-kpi-inactive" />
        <Kpi label="Recently Active" value={k.recently_active} icon="Activity" tone="text-indigo-600" testid="crm-kpi-recent" />
        <Kpi label="Leads Converted" value={k.leads_converted} icon="ArrowUpRight" tone="text-emerald-600" onClick={() => navigate("/crm/active-clients")} testid="crm-kpi-converted" />
        <Kpi label="Estimates Accepted" value={k.estimates_accepted} icon="Calculator" tone="text-blue-600" testid="crm-kpi-estimates" />
        <Kpi label="Engagements Signed" value={k.engagements_signed} icon="FileSignature" tone="text-violet-600" testid="crm-kpi-engagements" />
        <Kpi label="Leads Needing Follow-Up" value={k.leads_needing_followup} icon="BellRing" tone={k.leads_needing_followup ? "text-amber-600" : "text-slate-900"} ring={k.leads_needing_followup ? "ring-amber-200" : "ring-slate-200"} onClick={() => navigate("/crm/leads")} testid="crm-kpi-followup" />
        <Kpi label="Clients w/ Open Engagements" value={k.clients_open_engagements} icon="Briefcase" tone="text-emerald-600" testid="crm-kpi-open-eng" />
        <Kpi label="Clients No Recent Activity" value={k.clients_no_recent_activity} icon="Clock" tone={k.clients_no_recent_activity ? "text-rose-600" : "text-slate-900"} ring={k.clients_no_recent_activity ? "ring-rose-200" : "ring-slate-200"} onClick={() => navigate("/crm/active-clients")} testid="crm-kpi-stale" />
      </div>

      {/* row 1: recently active + needing follow-up */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Panel title="Recently Active Contacts" icon="Activity" tone="text-indigo-600" testid="crm-widget-recent">
          <div className="space-y-0.5">
            {(d?.recently_active || []).length === 0 && <div className="text-sm text-slate-400">No activity yet.</div>}
            {(d?.recently_active || []).map((c) => <ContactRow key={c.id} c={c} onOpen={open} />)}
          </div>
        </Panel>
        <Panel title="Leads Needing Follow-Up" icon="BellRing" tone="text-amber-600" testid="crm-widget-followup"
          action={<button onClick={() => navigate("/crm/leads")} className="ml-auto text-xs font-medium text-blue-600 hover:underline">View all</button>}>
          <div className="space-y-0.5">
            {(d?.needing_followup || []).length === 0 && <div className="text-sm text-slate-400">All leads have recent activity. 🎉</div>}
            {(d?.needing_followup || []).map((c) => <ContactRow key={c.id} c={c} onOpen={open} />)}
          </div>
        </Panel>
      </div>

      {/* row 2: recently converted + source + team */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Recently Converted Clients" icon="ArrowUpRight" tone="text-emerald-600" testid="crm-widget-converted">
          <div className="space-y-0.5">
            {(d?.recently_converted || []).length === 0 && <div className="text-sm text-slate-400">No recent conversions.</div>}
            {(d?.recently_converted || []).map((c) => <ContactRow key={c.id} c={c} onOpen={open} />)}
          </div>
        </Panel>

        <Panel title="Contact Source" icon="Radio" tone="text-sky-600" testid="crm-widget-source">
          <div className="space-y-2.5">
            {sources.length === 0 && <div className="text-sm text-slate-400">No data.</div>}
            {sources.map((s) => (
              <div key={s.source} className="flex items-center gap-3" data-testid={`crm-source-${s.source}`}>
                <span className="text-sm text-slate-600 w-32 shrink-0 truncate">{SOURCE_LABELS[s.source] || s.source}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-sky-500" style={{ width: `${Math.round((s.count / maxSrc) * 100)}%` }} />
                </div>
                <span className="text-sm font-semibold text-slate-700 w-8 text-right">{s.count}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Team Assignment" icon="Users" tone="text-blue-600" testid="crm-widget-teams">
          <div className="space-y-2.5">
            {teamSummary.length === 0 && <div className="text-sm text-slate-400">No assignments.</div>}
            {teamSummary.map((t) => (
              <div key={t.team} className="flex items-center justify-between" data-testid={`crm-team-${t.team}`}>
                <span className="text-sm text-slate-600">{t.name}</span>
                <span className="text-sm font-semibold text-slate-800 bg-slate-100 rounded-full px-2.5 py-0.5">{t.count}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
